import React from "react";
import styles from "./ErrorBoundary.module.css";

interface FallbackProps {
  error: Error;
  reset: () => void;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static displayName = "ErrorBoundary";

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  componentDidCatch(_error: Error, _errorInfo: React.ErrorInfo) {
    // You can log the error to an error reporting service here
    // Console logging removed for production builds
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { hasError, error } = this.state;

    if (!hasError || !error) {
      return this.props.children;
    }

    // If a custom fallback component is provided, use it
    if (this.props.fallback) {
      const FallbackComponent = this.props.fallback;
      return <FallbackComponent error={error} reset={this.reset} />;
    }

    // Default fallback UI
    return (
      <div className={styles.error}>
        <h2>Oops! Something went wrong</h2>
        <p>An unexpected error occurred. Please try refreshing the page.</p>
        <details>
          <summary>Error details</summary>
          {error.message}
          {"\n"}
          {error.stack}
        </details>
        <button onClick={this.reset}>Try again</button>
      </div>
    );
  }
}

export default ErrorBoundary;
